import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Container,
  Paper,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Snackbar,
  Breadcrumbs,
  Link,
  FormControlLabel,
  Switch,
  Autocomplete,
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LinkIcon from '@mui/icons-material/Link';
import EventIcon from '@mui/icons-material/Event';
import NotificationsIcon from '@mui/icons-material/Notifications';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import {
  getTeam,
  getTeamMembers,
  updateTeamMember,
  removeTeamMember,
  createTeamInvite,
  generateTeamInviteLink,
  updateTeam,
  deleteTeam,
  getTeamSessions,
} from '../utils/teamsApi';
import {
  getSlackStatus,
  getSlackChannels,
  getTeamSlackSettings,
  updateTeamSlackSettings,
  testSlackNotification,
} from '../utils/slackApi';

const TeamDetailPage = () => {
  const { teamId } = useParams();
  const { user } = useAuth();
  const { setCurrentTeam } = useWorkspace();
  const navigate = useNavigate();

  const [team, setTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [saving, setSaving] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Invite dialog
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [creatingInvite, setCreatingInvite] = useState(false);

  // Member actions
  const [memberMenuAnchor, setMemberMenuAnchor] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);

  const [successMessage, setSuccessMessage] = useState(null);
  const [actionError, setActionError] = useState(null);

  // Slack integration state
  const [slackConnected, setSlackConnected] = useState(false);
  const [slackChannels, setSlackChannels] = useState([]);
  const [loadingSlackChannels, setLoadingSlackChannels] = useState(false);
  const [slackSettings, setSlackSettings] = useState({
    slackChannelId: null,
    slackChannelName: null,
    slackNotificationsEnabled: false,
  });
  const [savingSlackSettings, setSavingSlackSettings] = useState(false);
  const [testingSlack, setTestingSlack] = useState(false);
  const [testSuccess, setTestSuccess] = useState(false);

  const fetchTeamData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [teamData, membersData, sessionsData, teamSlackSettings] = await Promise.all([
        getTeam(teamId),
        getTeamMembers(teamId),
        getTeamSessions(teamId).catch(() => []),
        getTeamSlackSettings(teamId).catch(() => ({
          slackChannelId: null,
          slackChannelName: null,
          slackNotificationsEnabled: false,
        })),
      ]);
      setTeam(teamData);
      setMembers(membersData);
      setSessions(sessionsData);
      setCurrentTeam(teamData);
      setEditName(teamData.name);
      setEditDescription(teamData.description || '');
      setSlackSettings(teamSlackSettings);

      // Check if Slack is connected for this org
      if (teamData.organization?.id) {
        try {
          const slackStatus = await getSlackStatus(teamData.organization.id);
          setSlackConnected(slackStatus.connected);
        } catch {
          setSlackConnected(false);
        }
      }
    } catch (err) {
      console.error('Error fetching team:', err);
      setError(err.response?.data?.message || 'Failed to load team');
    } finally {
      setLoading(false);
    }
  }, [teamId, setCurrentTeam]);

  useEffect(() => {
    fetchTeamData();
  }, [fetchTeamData]);

  // Fetch Slack channels when the Settings tab is opened
  useEffect(() => {
    if (activeTab === 2 && slackConnected && slackChannels.length === 0) {
      fetchSlackChannels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, slackConnected]);

  const canManage = team && ['owner', 'admin', 'org_admin'].includes(team.userRole);
  const isOwner = team && (team.userRole === 'owner' || team.userRole === 'org_admin');

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await updateTeam(teamId, {
        name: editName.trim(),
        description: editDescription.trim() || null,
      });
      setSuccessMessage('Team updated successfully');
      setEditDialogOpen(false);
      fetchTeamData();
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to update team');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteTeam(teamId);
      navigate(`/organizations/${team.organization.id}`);
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to delete team');
      setDeleting(false);
    }
  };

  const handleCreateInvite = async () => {
    setCreatingInvite(true);
    try {
      const invite = await createTeamInvite(teamId);
      setInviteLink(generateTeamInviteLink(invite.token));
      setInviteDialogOpen(true);
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to create invite');
    } finally {
      setCreatingInvite(false);
    }
  };

  const handleCopyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setSuccessMessage('Invite link copied to clipboard!');
  };

  const handleMemberMenuOpen = (event, member) => {
    setMemberMenuAnchor(event.currentTarget);
    setSelectedMember(member);
  };

  const handleMemberMenuClose = () => {
    setMemberMenuAnchor(null);
    setSelectedMember(null);
  };

  const handleChangeRole = async (newRole) => {
    if (!selectedMember) return;
    try {
      await updateTeamMember(teamId, selectedMember.userId, { role: newRole });
      setSuccessMessage('Member role updated');
      fetchTeamData();
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to update role');
    }
    handleMemberMenuClose();
  };

  const handleRemoveMember = async () => {
    if (!selectedMember) return;
    try {
      await removeTeamMember(teamId, selectedMember.userId);
      setSuccessMessage('Member removed');
      fetchTeamData();
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to remove member');
    }
    handleMemberMenuClose();
  };

  const handleOpenSession = (session) => {
    navigate(`/meeting/${session.sessionId}`);
  };

  const fetchSlackChannels = async () => {
    if (!team?.organization?.id || !slackConnected) return;

    setLoadingSlackChannels(true);
    try {
      const channels = await getSlackChannels(team.organization.id);
      setSlackChannels(channels);
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to fetch Slack channels');
    } finally {
      setLoadingSlackChannels(false);
    }
  };

  const handleSaveSlackSettings = async () => {
    setSavingSlackSettings(true);
    try {
      const updatedSettings = await updateTeamSlackSettings(teamId, slackSettings);
      setSlackSettings(updatedSettings);
      setSuccessMessage('Slack notification settings saved');
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to save Slack settings');
    } finally {
      setSavingSlackSettings(false);
    }
  };

  const handleTestSlackNotification = async () => {
    if (!slackSettings.slackChannelId) {
      setActionError('Please select a channel first');
      return;
    }

    setTestingSlack(true);
    setTestSuccess(false);
    try {
      await testSlackNotification(team.organization.id, slackSettings.slackChannelId);
      setTestSuccess(true);
      setSuccessMessage('Test notification sent! Check your Slack channel.');
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to send test notification');
    } finally {
      setTestingSlack(false);
    }
  };

  const handleChannelChange = (event, newValue) => {
    setTestSuccess(false); // Reset test success when channel changes
    if (newValue) {
      setSlackSettings({
        ...slackSettings,
        slackChannelId: newValue.id,
        slackChannelName: newValue.name,
      });
    } else {
      setSlackSettings({
        ...slackSettings,
        slackChannelId: null,
        slackChannelName: null,
      });
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'owner': return 'error';
      case 'admin': return 'warning';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'paused': return 'warning';
      case 'ended': return 'default';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 4 }}>{error}</Alert>
      </Container>
    );
  }

  if (!team) return null;

  return (
    <Container maxWidth="lg">
      <Paper elevation={3} sx={{ p: 3, mt: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link component={RouterLink} to="/organizations" underline="hover" color="inherit">
            Organizations
          </Link>
          <Link
            component={RouterLink}
            to={`/organizations/${team.organization?.id}`}
            underline="hover"
            color="inherit"
          >
            {team.organization?.name}
          </Link>
          <Typography color="text.primary">{team.name}</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Avatar sx={{ width: 64, height: 64, mr: 2, bgcolor: 'secondary.main' }}>
            <GroupIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4">{team.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {team.description || 'No description'} - {members.length} members
            </Typography>
          </Box>
          {canManage && (
            <Box>
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={() => setEditDialogOpen(true)}
                sx={{ mr: 1 }}
              >
                Edit
              </Button>
              {isOwner && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  Delete
                </Button>
              )}
            </Box>
          )}
        </Box>

        {/* Tabs */}
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3 }}>
          <Tab label="Sessions" icon={<EventIcon />} iconPosition="start" />
          <Tab label="Members" icon={<PersonIcon />} iconPosition="start" />
          {canManage && <Tab label="Settings" icon={<SettingsIcon />} iconPosition="start" />}
        </Tabs>

        {/* Sessions Tab */}
        {activeTab === 0 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Sessions</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                component={RouterLink}
                to={`/create-session?teamId=${teamId}`}
              >
                New Session
              </Button>
            </Box>

            {sessions.length > 0 ? (
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 2,
              }}>
                {sessions.map((session) => (
                  <Card
                    key={session.sessionId}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { boxShadow: 6 },
                    }}
                    onClick={() => handleOpenSession(session)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="h6">{session.name}</Typography>
                        <Chip
                          label={session.status}
                          size="small"
                          color={getStatusColor(session.status)}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        Created {new Date(session.createdAt).toLocaleDateString()}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <EventIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">No sessions yet</Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Members Tab */}
        {activeTab === 1 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Members</Typography>
              {canManage && (
                <Button
                  variant="contained"
                  startIcon={<LinkIcon />}
                  onClick={handleCreateInvite}
                  disabled={creatingInvite}
                >
                  {creatingInvite ? <CircularProgress size={24} /> : 'Create Invite Link'}
                </Button>
              )}
            </Box>

            <List>
              {members.map((member) => (
                <ListItem key={member.id}>
                  <ListItemAvatar>
                    <Avatar>{member.user?.name?.charAt(0) || '?'}</Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={member.user?.name || 'Unknown'}
                    secondary={member.user?.email}
                  />
                  <Chip
                    label={member.role}
                    size="small"
                    color={getRoleBadgeColor(member.role)}
                    sx={{ mr: 1 }}
                  />
                  {canManage && member.userId !== user?.id && (
                    <ListItemSecondaryAction>
                      <IconButton onClick={(e) => handleMemberMenuOpen(e, member)}>
                        <MoreVertIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Settings Tab */}
        {activeTab === 2 && canManage && (
          <Box>
            <Typography variant="h6" gutterBottom>
              <NotificationsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Slack Notifications
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Receive notifications in Slack when async voting rounds complete without consensus.
            </Typography>

            {!slackConnected ? (
              <Alert severity="info">
                Slack is not connected for this organization. An admin can connect Slack from the{' '}
                <Link
                  component={RouterLink}
                  to={`/organizations/${team.organization?.id}/settings`}
                >
                  organization settings
                </Link>
                .
              </Alert>
            ) : (
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Autocomplete
                  options={slackChannels}
                  getOptionLabel={(option) => `#${option.name}`}
                  value={slackChannels.find((c) => c.id === slackSettings.slackChannelId) || null}
                  onChange={handleChannelChange}
                  loading={loadingSlackChannels}
                  disabled={loadingSlackChannels}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      #{option.name}
                      {option.isPrivate && (
                        <Chip label="Private" size="small" sx={{ ml: 1 }} />
                      )}
                      {option.isMember && (
                        <Chip label="Bot member" size="small" color="success" sx={{ ml: 1 }} />
                      )}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Notification Channel"
                      placeholder="Search channels..."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingSlackChannels ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  sx={{ mb: 2 }}
                />

                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    Don&apos;t see your channel?
                  </Typography>
                  <Typography variant="body2">
                    Private channels only appear after you invite the bot. In Slack, go to your channel and type:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', px: 1, py: 0.5, mt: 1, borderRadius: 1 }}>
                    /invite @YourSlackAppName
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Then click Refresh below. The app name is whatever you named it when creating the Slack app.
                  </Typography>
                </Alert>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    {loadingSlackChannels
                      ? 'Loading channels...'
                      : `${slackChannels.length} channel${slackChannels.length !== 1 ? 's' : ''} available`}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={fetchSlackChannels}
                    disabled={loadingSlackChannels}
                    sx={{ ml: 2 }}
                  >
                    Refresh
                  </Button>
                </Box>

                <FormControlLabel
                  control={
                    <Switch
                      checked={slackSettings.slackNotificationsEnabled}
                      onChange={(e) =>
                        setSlackSettings({
                          ...slackSettings,
                          slackNotificationsEnabled: e.target.checked,
                        })
                      }
                      disabled={!slackSettings.slackChannelId}
                    />
                  }
                  label="Enable notifications for no-consensus votes"
                  sx={{ mb: 3, display: 'block' }}
                />

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    onClick={handleSaveSlackSettings}
                    disabled={savingSlackSettings}
                  >
                    {savingSlackSettings ? <CircularProgress size={24} /> : 'Save Settings'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleTestSlackNotification}
                    disabled={testingSlack || !slackSettings.slackChannelId}
                    color={testSuccess ? 'success' : 'primary'}
                    startIcon={testSuccess ? <CheckCircleIcon /> : null}
                  >
                    {testingSlack ? <CircularProgress size={24} /> : testSuccess ? 'Test Sent!' : 'Send Test Notification'}
                  </Button>
                </Box>

                {slackSettings.slackChannelId && slackSettings.slackNotificationsEnabled && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    Notifications will be sent to <strong>#{slackSettings.slackChannelName}</strong> when
                    async voting reveals without consensus.
                  </Alert>
                )}
              </Paper>
            )}
          </Box>
        )}
      </Paper>

      {/* Edit Team Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Team</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Team Name"
            fullWidth
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            required
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={saving || !editName.trim()}
          >
            {saving ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Team Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Team</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete team <strong>{team.name}</strong>?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite Link Dialog */}
      <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Team Invite Link</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Share this link to invite people to your team. They will automatically be added to the organization as well.
          </DialogContentText>
          <TextField
            fullWidth
            value={inviteLink}
            InputProps={{ readOnly: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)}>Close</Button>
          <Button onClick={handleCopyInviteLink} variant="contained">
            Copy Link
          </Button>
        </DialogActions>
      </Dialog>

      {/* Member Actions Menu */}
      <Menu
        anchorEl={memberMenuAnchor}
        open={Boolean(memberMenuAnchor)}
        onClose={handleMemberMenuClose}
      >
        {isOwner && selectedMember?.role !== 'owner' && (
          <MenuItem onClick={() => handleChangeRole('owner')}>Make Owner</MenuItem>
        )}
        {selectedMember?.role !== 'admin' && (
          <MenuItem onClick={() => handleChangeRole('admin')}>Make Admin</MenuItem>
        )}
        {selectedMember?.role !== 'member' && (
          <MenuItem onClick={() => handleChangeRole('member')}>Make Member</MenuItem>
        )}
        {selectedMember?.role !== 'viewer' && (
          <MenuItem onClick={() => handleChangeRole('viewer')}>Make Viewer</MenuItem>
        )}
        <MenuItem onClick={handleRemoveMember} sx={{ color: 'error.main' }}>
          Remove from Team
        </MenuItem>
      </Menu>

      <Snackbar
        open={!!successMessage || !!actionError}
        autoHideDuration={6000}
        onClose={() => { setSuccessMessage(null); setActionError(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={successMessage ? 'success' : 'error'}>
          {successMessage || actionError}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default TeamDetailPage;
